import { cva } from 'class-variance-authority';

/**
 * Dialog class recipes.
 *
 * Deliberately contain ZERO `dark:` variants. Colour comes entirely from the `--ds-dialog-*` slot
 * variables, which the `.ds-dialog` class in `components/styles/design-system.css` sets per theme.
 * One class list therefore serves both themes.
 *
 * Ported from Figma node 544:20045, page `Dialog [Reviewed][Semantic]`, re-read 2026-09-21 after the
 * set was reworked onto the semantic layer. Geometry read off the `Size=md, Type=Default` cell,
 * whose Modal frame is still node 570:23911 — the rework kept the child ids — and which every other
 * cell shares bar its width: padding 20, radius 16, a 1px inside stroke.
 *
 * THE COLUMN GAPS ARE NO LONGER A DEPARTURE. This used to carry a note that the code drew 16px above
 * the body and 24px below it where Figma drew a uniform 32px, pending a Figma update. That update has
 * landed: the set's Modal frame now has `itemSpacing: 16` and its Footer a `paddingTop: 8`, which is
 * 16 above and 24 below exactly as here, and the set's description states the pair outright. The two
 * agree, and the gaps live on the header and footer rather than as one `gap` only because the two
 * numbers differ.
 */

/**
 * The scrim.
 *
 * `--background-backdrop` rather than a slot: the reworked set binds the backdrop to
 * `background/backdrop/default`, which resolves to `rgb(0 0 0 / 0.5)` in BOTH modes, so a slot could
 * only ever hold one value. It was a literal `bg-black/50` here until 2026-09-21, which was the
 * faithful reading while the set drew the scrim as a raw fill rather than a role — the painted
 * colour is unchanged, only its provenance.
 *
 * `z-1300` is MUI's own modal z-index. A DS 2.0 dialog and a legacy MUI one are the same kind of
 * thing and belong on the same layer, so whichever opened last sits on top by DOM order — rather
 * than a DS dialog permanently outranking an MUI one, or vice versa.
 *
 * The POPUP shares this exact value for the same reason — see the note on `dialogPopupRecipe`.
 */
export const dialogBackdropRecipe = cva([
  'fixed inset-0 z-1300',
  'bg-[var(--background-backdrop)]',
  /**
   * The scrim fades with the surface it dims — `--motion-surface` in, `--motion-surface-exit` out.
   *
   * Opacity ONLY, and unconditionally: a scrim appearing is the one part of a dialog's entrance that
   * carries no movement at all, so there is nothing here a vestibular-sensitive user needs protecting
   * from and no reason to branch. `docs/motion.md`'s reduced-motion rule is about MOVEMENT; a
   * cross-fade is the gentler variant, not the thing being suppressed.
   *
   * `data-starting-style` and `data-ending-style` are Base UI's two lifecycle hooks: the first is
   * present for one frame as the element mounts, the second for the duration of the close. The close
   * hook is also what keeps the scrim mounted long enough to fade out — Base UI defers the unmount
   * until the transitions on the element have finished.
   */
  'transition-opacity duration-[var(--motion-surface)] ease-[var(--motion-surface-ease)]',
  'data-[ending-style]:duration-[var(--motion-surface-exit)]',
  'data-[ending-style]:ease-[var(--motion-surface-exit-ease)]',
  'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
]);

/**
 * The modal surface.
 *
 * Height is bounded by the VIEWPORT rather than a fixed maximum, which is the behavioural point of
 * the redesign (design note 580:34306). `dvh` rather than `vh` so a mobile browser's collapsing
 * address bar cannot push the footer off-screen — the exact bug this component exists to fix.
 *
 * The 20px inset on both axes comes off the design's own overflowing example (580:30593: a 706px
 * modal in a 742px frame) and its mobile one (593:39201: a 360px modal in a 402px frame).
 */
export const dialogPopupRecipe = cva(
  [
    'ds-dialog group/dialog',
    /**
     * `z-1300`, the SAME layer as the backdrop — deliberately not one above it.
     *
     * The popup does not need a higher z-index to clear its own backdrop: the two are siblings in one
     * portal and the popup renders second, so equal z-index already stacks it on top by DOM order.
     *
     * Taking `z-1301` instead broke every MUI popover opened from inside a DS dialog. An MUI `Select`
     * or `Menu` is a modal at 1300, so it lost to 1301 and rendered BEHIND the dialog that owned it —
     * which is what `StartNatteringModal`'s three selects did. Sharing the layer restores the rule the
     * backdrop's note describes: same layer, DOM order decides, so a menu opened later wins.
     *
     * DS 2.0's own popovers sit at 1400 (`Menu.recipe`, `Select.recipe`) and are unaffected either
     * way; this only matters for the legacy MUI components still rendered inside a refreshed dialog.
     */
    'fixed top-1/2 left-1/2 z-1300 -translate-x-1/2 -translate-y-1/2',
    // No `gap`: the two column gaps differ (16 above the body, 24 below it), so they live as margins
    // on the header and footer instead. See `dialogHeaderRecipe` / `dialogFooterRecipe`.
    'flex flex-col',
    'max-h-[calc(100dvh-40px)] max-w-[calc(100vw-40px)]',
    'rounded-2xl p-5',
    'bg-[var(--ds-dialog-surface)]',
    /**
     * The rim the 2026-09-21 rework added, replacing elevation on this component — a 1px inside
     * stroke at `border/primary/default` on the Modal frame in all six cells.
     *
     * `border-solid` is written out for CONSISTENCY with the rest of this layer, not because the rim
     * needs it. `ContentPanel`, `Accordion`, `Alert`, `TextInput` and `Radio` all spell it, so this
     * matches them; measured in Chromium on 2026-09-21, dropping it changes nothing, because
     * Tailwind v4's width utilities set `border-style: var(--tw-border-style)` and that property's
     * initial value is `solid`. Worth recording because the obvious reasoning is wrong: this
     * stylesheet DOES omit Tailwind's preflight (see the note atop `design-system.css`), so the
     * v3-era conclusion would be that a width with no style paints nothing — and under v4 it paints
     * fine. Verified by removing the class and reading `getComputedStyle`, not by reading the docs.
     *
     * The popup is border-box (`index.css` sets it on `*`, `*::before` and `*::after`), and Figma's
     * stroke is INSIDE its frame, so the two agree exactly: `w-[600px]` plus this border is 600px
     * outer, matching the set's 600px Modal. The 2px comes off the content box, not the outer size.
     */
    'border-solid border-[length:1px] border-[var(--ds-dialog-border)]',
    'font-sans',
    // The popup is focusable so Base UI can park focus on it when a dialog opened by touch has no
    // tabbable content; it is not a control, so it gets no focus ring of its own.
    'focus:outline-none',

    /**
     * ── The entrance and the exit ──────────────────────────────────────────────────────────────
     *
     * `--motion-surface` (250ms) in, `--motion-surface-exit` (200ms) out. The exit is shorter
     * because the user has already decided: getting out of the way is not a thing to be savoured.
     *
     * TWO VARIANTS, WHICH IS THE POINT OF THE SPLIT BELOW.
     *
     *   default          opacity 0 -> 1 AND scale 0.96 -> 1
     *   reduced motion   opacity only
     *
     * The fade is unconditional and the scale is `motion-safe:`. Reduced motion means gentler, not
     * nothing — a cross-fade aids comprehension and triggers nobody, while a growing 600px surface
     * is exactly the vestibular case WCAG 2.3.3 is about. Deleting both would leave a modal that
     * teleports in, which is a worse experience for the user who asked for less motion.
     *
     * `motion-safe:` rather than a `motion-reduce:` suppression, and this is the trap the whole
     * convention exists for: a lifecycle-qualified scale utility compiles to a class PLUS an
     * attribute, specificity (0,2,0), and `motion-reduce:transition-none` is a bare class inside a
     * media query — (0,1,0), because a media query contributes no specificity of its own. The
     * suppression loses. Written the other way round the scale would play for a reduced-motion user
     * and every test would still be green. Measured in Chromium, Firefox and WebKit; see
     * `docs/motion-lifecycle-findings.md`.
     *
     * 0.96, NOT 0. Nothing in the real world appears out of nothing, and a surface that grows from
     * a point reads as a special effect rather than as a window opening. 4% is the amount that
     * makes the dialog feel like it settles into place without ever looking like it moved.
     *
     * `transform-origin` is deliberately left at its `center` default, unlike every popup in this
     * layer. A modal has no trigger to grow from — it appears in the middle of the viewport, so the
     * middle is where it belongs. The `-translate-*` pair above centres it and is untouched by this:
     * Tailwind v4 compiles `translate-*` and `scale-*` to the INDIVIDUAL `translate` and `scale`
     * properties rather than to one `transform` string, so the two compose instead of overwriting
     * each other — and `scale` alone is what transitions.
     */
    'transition-[opacity,scale] duration-[var(--motion-surface)] ease-[var(--motion-surface-ease)]',
    'data-[ending-style]:duration-[var(--motion-surface-exit)]',
    'data-[ending-style]:ease-[var(--motion-surface-exit-ease)]',
    'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
    'motion-safe:data-[starting-style]:scale-[0.96] motion-safe:data-[ending-style]:scale-[0.96]',
  ],
  {
    variants: {
      size: {
        sm: 'w-[360px]',
        md: 'w-[600px]',
        lg: 'w-[900px]',
      },
    },
    defaultVariants: { size: 'md' },
  }
);

/**
 * The header row.
 *
 * Height is NOT pinned to Figma's 20px. Every cell in the set draws a 20px-tall box around 26px
 * line-height text, which works only for the one-line placeholder title it was drawn with; the
 * design's own overflowing example grows the same header to 60px. Auto height is the reading that
 * survives a title that wraps.
 *
 * `mb-4` is the 16px column gap above the body, per design's review of the Chromatic build
 * (HNS-337). It lives here rather than as a `gap` on the popup because the gap below the body is a
 * different number — see `dialogFooterRecipe`. Note the published Figma set still draws a uniform
 * 32px; design is updating it to match these two values, not the other way round.
 */
export const dialogHeaderRecipe = cva(['mb-4 flex shrink-0 items-center gap-2']);

/**
 * `m-0` is load-bearing, not tidiness. `Dialog.Title` renders an `<h2>`, and the token layer
 * deliberately omits Tailwind's preflight so it cannot reset the legacy MUI baseline — so the
 * browser's own `h2 { margin: 0.83em 0 }` applies and pads the header by ~13px top and bottom. The
 * other DS 2.0 components never hit this because none of them render a heading.
 */
export const dialogTitleRecipe = cva(['m-0 min-w-0 flex-1', 'text-heading-xs text-[var(--ds-dialog-title)]']);

/**
 * The body — the one scrollable region.
 *
 * `flex-auto` rather than `flex-1`: `flex-1` sets `flex-basis: 0%`, which would make the popup's
 * auto height ignore the body's content and collapse it. `flex-auto` keeps `basis: auto`, so a short
 * dialog is content-sized and a tall one shrinks against `max-h` and scrolls. An explicit `min-h` is
 * what permits that shrink at all — on the flex default of `min-height: auto` the item refuses to go
 * below its content height and the popup grows past the viewport, footer and all.
 *
 * `overscroll-contain` stops a flick at the end of the list scrolling the page behind.
 *
 * That floor is `min-h-6` — 24px, the height of ONE FADE — rather than the `min-h-0` this shipped
 * with. 24px is nothing against a viewport, so the `max-h` case above still shrinks as far as it
 * needs to, and it closes a hole the fades opened: a fade's own border box is 24px tall, and its
 * cancelling negative margin only moves the SIBLING, so on a scrollport shorter than that the fade
 * itself became scrollable overflow. A one-line body measured `clientHeight: 20` against
 * `scrollHeight: 24` and pinned the bottom fade on with nothing to scroll to — visible on the
 * one-sentence "Remove this background?" confirm (HNS-367). Two lines already cleared 24px, which is
 * why only the shortest dialogs in the app showed it.
 *
 * Fixed at the measurement's SOURCE rather than by widening the comparison's tolerance: the fade
 * mechanism needs a scrollport it fits in, and a scrollport shorter than a fade was also 4px of real
 * (if pointless) scrollability, which a tolerance would have hidden rather than removed.
 *
 * The scrollbar is Figma's (node I580:30599;580:34447): a thin thumb over no track. Reproduced with
 * the standard properties rather than a `::-webkit-scrollbar` block, which Firefox ignores.
 *
 * `-m-1`, with `p-1` on `dialogBodyContentRecipe`, is room for focus rings: a scroll area clips what
 * paints outside it, and the outer ring is a 4px box-shadow (`--effect-focus-outer-spread`). The
 * scrollport grows 4px into the popup's padding and the content moves back, so nothing visible
 * shifts. Guarded by the `FocusRingFitsInsideTheBody` story.
 */
export const dialogBodyRecipe = cva([
  'relative -m-1 flex-auto min-h-6 overflow-y-auto overscroll-contain',
  '[scrollbar-width:thin] [scrollbar-color:var(--ds-dialog-scrollbar)_transparent]',
]);

/**
 * The body's leading paragraph.
 *
 * `m-0` is load-bearing for the same reason it is on the title: `Dialog.Description` renders a `<p>`,
 * and the token layer omits Tailwind's preflight so it cannot reset the legacy MUI baseline — leaving
 * the browser's own `p { margin: 1em 0 }` to add 16px above and below. Colour and size are inherited
 * from `dialogBodyContentRecipe`, since the paragraph IS the body's default text.
 */
export const dialogDescriptionRecipe = cva(['m-0']);

/**
 * The children wrapper.
 *
 * The 8px row gap is a CODE decision, not a reading of the set — stated plainly because this used to
 * cite node 570:23914 as its authority and that citation was wrong after the rework. 570:23914 is
 * the Description frame, and it has `itemSpacing: 0`; the set's `Content` slot is empty in all six
 * cells, so the set specifies nothing about how a caller's rows are spaced. 8px is the DS `x2` step
 * and the value this shipped with, kept because changing it would move every dialog body for no
 * reason the design gives.
 *
 * `p-1` hands back the 4px `dialogBodyRecipe`'s `-m-1` takes; see that recipe.
 */
export const dialogBodyContentRecipe = cva(['flex flex-col gap-2 p-1', 'text-body-md text-[var(--ds-dialog-body)]']);

/**
 * The fades at the body's edges, which is how "scrolled content reads as passing behind the header
 * and footer" is delivered (design note 580:34306).
 *
 * `sticky` with a cancelling negative margin, rather than absolute positioning: the fade has to stay
 * pinned to the scrollport while the content moves under it, and contribute no height of its own.
 * Absolute positioning against the popup would need the header's height measured, and that height
 * changes when the title wraps.
 *
 * Shown only when there is something out of view in that direction — `Dialog` sets the
 * `data-overflow-*` attributes on the POPUP off the real scroll metrics, so a short dialog has no
 * fades at all and matches the flat cells in the component set exactly.
 */
export const dialogFadeRecipe = cva(
  [
    'pointer-events-none sticky z-10 block h-6 shrink-0',
    'opacity-0 transition-opacity duration-[var(--motion-affordance)] ease-[var(--motion-affordance-ease)] motion-reduce:transition-none',
  ],
  {
    variants: {
      edge: {
        top: [
          'top-0 -mb-6',
          'bg-linear-to-b from-[var(--ds-dialog-surface)] to-transparent',
          'group-data-[overflow-top]/dialog:opacity-100',
        ],
        bottom: [
          'bottom-0 -mt-6',
          'bg-linear-to-t from-[var(--ds-dialog-surface)] to-transparent',
          'group-data-[overflow-bottom]/dialog:opacity-100',
        ],
      },
    },
  }
);

/**
 * Figma's footer: right-aligned, 10px gap, up to two actions (570:23917).
 *
 * `mt-6` is the 24px column gap below the body, per design's review (HNS-337). Carried by the footer
 * rather than the body on purpose: a dialog with no actions renders no footer, and the gap should go
 * with it rather than leave 24px of dead space above the bottom padding.
 */
export const dialogFooterRecipe = cva(['mt-6 flex shrink-0 items-center justify-end gap-2.5']);
